import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/useToast';
import { api, formatApiError } from '@/lib/api';
import { openAiVraag, pollInterval } from '@/lib/melden/gesprek';
import { nieuweId } from '@/lib/melden/id';
import { AiBerichtZin } from './AiBerichtZin';
import { AntwoordFormulier } from './AntwoordFormulier';

/**
 * The state and the conversation of one melding, as the customer may see it
 * (Qontrol returns only voor_klant messages). Polls only while mounted and
 * only when asked: the panel right after sending (P5), never the list page.
 * Message text is rendered as React text nodes, never as HTML. kanAntwoorden=false
 * (a colleague's melding in the team view) hides the answer: PriceQ takes
 * answers only on the person's own melding. Every AI message says it is AI
 * (AI Act art. 50(1), spec P7); "Spreek een mens" only where answering is allowed
 * and not while polling (the panel carries its own button).
 */
export function MeldingGesprek({ externalId, poll, kanAntwoorden = true }: {
  externalId: string;
  poll: boolean;
  kanAntwoorden?: boolean;
}) {
  const { t } = useTranslation();
  const [sinds] = useState(() => Date.now());
  // One external_id per conversation: a double click on "Spreek een mens" is one message (Qontrol dedups on it).
  const [mensId] = useState(() => nieuweId());
  const mens = useMutation({
    mutationFn: () => api.meldingen.bericht(externalId, { external_id: mensId, soort: 'wil_mens', tekst: '', bijlagen: [] }),
    onError: (e) => toast.error(t('melden.fout'), { description: formatApiError(e, t('melden.fout')) }),
  });
  const detail = useQuery({
    queryKey: ['meldingen', 'detail', externalId],
    queryFn: () => api.meldingen.detail(externalId),
    refetchInterval: poll ? () => pollInterval(sinds, Date.now()) : false,
    retry: false,
  });

  if (detail.isError) {
    return <p role="alert" className="mt-3 text-[13px] text-danger">{t('melden.gesprek.fout')}</p>;
  }
  if (!detail.data) return null;
  const d = detail.data;
  const vraag = openAiVraag(d.berichten);

  return (
    <section aria-live="polite" className="mt-3 space-y-2">
      <p className="text-[12.5px] text-text-muted">
        {d.nummer !== null ? `${t('melden.nummer', { nummer: d.nummer })} · ` : ''}
        {t(`melden.stand.${d.stand}`, { defaultValue: t('melden.stand.in_behandeling') })}
      </p>
      {d.berichten.length > 0 && (
        <ul className="space-y-2">
          {d.berichten.map((b) => (
            <li key={b.external_id} className="rounded-sm border border-border-subtle bg-bg-primary px-3 py-2">
              <span className="block text-[11.5px] text-text-muted">
                {t(`melden.gesprek.van.${b.van}`, { defaultValue: '' })}
              </span>
              <span className="whitespace-pre-wrap text-[13px] text-text-primary">{b.tekst}</span>
              {/* The polling panel (MeldingVerstuurd) has its own "Spreek een mens" right above: no second id there. */}
              <AiBerichtZin
                van={b.van}
                onSpreekMens={kanAntwoorden && !poll ? () => mens.mutate() : undefined}
                bezig={mens.isPending || mens.isSuccess}
              />
            </li>
          ))}
        </ul>
      )}
      {mens.isSuccess && <output className="block text-[13px] text-text-secondary">{t('melden.mensGevraagd')}</output>}
      {vraag && kanAntwoorden && <AntwoordFormulier externalId={externalId} vraag={vraag} />}
    </section>
  );
}
